import React from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LanguageProvider, useTranslation } from './i18n';
import axios from 'axios';
import './styles/addison-theme.css';

// Components
import Logo from './components/Logo';
import LanguageSwitcher from './components/LanguageSwitcher';

// Pages
import Landing from './pages/Landing';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import Inbox from './pages/Inbox';
import Compose from './pages/Compose';
import TempMail from './pages/TempMail';
import PGPManager from './pages/PGPManager';
import Upgrade from './pages/Upgrade';
import Settings from './pages/Settings';
import Messages from './pages/Messages';
import About from './pages/About';
import Contact from './pages/Contact';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import Admin from './pages/Admin';

axios.defaults.baseURL = 'https://tormal-api.winter-mouse-2fc0.workers.dev';

// Navigation Component
const Navigation = () => {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();

  const isActive = (path) => location.pathname === path ? 'active' : '';

  return (
    <nav className="navbar">
      <div style={{ 
        maxWidth: '1400px', 
        margin: '0 auto', 
        padding: '0 24px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <Link to="/" style={{ textDecoration: 'none' }}>
          <Logo size="medium" />
        </Link>

        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <LanguageSwitcher />
          
          {user ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Link to="/dashboard" className={`nav-link ${isActive('/dashboard')}`}>
                {t('nav.dashboard')}
              </Link>
              <Link to="/inbox" className={`nav-link ${isActive('/inbox')}`}>
                {t('nav.inbox')}
              </Link>
              <Link to="/compose" className={`nav-link ${isActive('/compose')}`}>
                {t('nav.compose')}
              </Link>
              <Link to="/tempmail" className={`nav-link ${isActive('/tempmail')}`}>
                {t('nav.tempmail')}
              </Link>
              <Link to="/pgp" className={`nav-link ${isActive('/pgp')}`}>
                {t('nav.pgp')}
              </Link>
              <div style={{ position: 'relative' }} className="dropdown">
                <button 
                  className="btn btn-ghost"
                  style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                  👤 {user.username}
                </button>
                <div className="dropdown-menu">
                  <Link to="/settings" className="dropdown-item">⚙️ {t('nav.settings')}</Link>
                  <Link to="/upgrade" className="dropdown-item">💎 {t('nav.upgrade')}</Link>
                  {user?.is_admin && <Link to="/admin" className="dropdown-item">👑 Admin</Link>}
                  <hr />
                  <button onClick={logout} className="dropdown-item">🚪 {t('nav.logout')}</button>
                </div>
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Link to="/about" className={`nav-link ${isActive('/about')}`}>
                {t('nav.about')}
              </Link>
              <Link to="/contact" className={`nav-link ${isActive('/contact')}`}>
                {t('nav.contact')}
              </Link>
              <Link to="/tempmail" className={`nav-link ${isActive('/tempmail')}`}>
                {t('nav.tempmail')}
              </Link>
              <Link to="/login" className="btn btn-ghost">{t('nav.login')}</Link>
              <Link to="/register" className="btn btn-primary">{t('nav.register')}</Link>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user } = useAuth();
  return user ? children : <Navigate to="/login" />;
};

// Footer Component
const Footer = () => {
  const { t } = useTranslation();
  
  return (
    <footer style={{ 
      borderTop: '1px solid var(--border)',
      padding: '40px 24px',
      marginTop: '60px',
      background: 'rgba(0,0,0,0.5)'
    }}>
      <div style={{ 
        maxWidth: '1400px', 
        margin: '0 auto',
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '40px'
      }}>
        <div>
          <Logo size="small" />
          <p style={{ color: 'var(--text-muted)', lineHeight: '1.8', marginTop: '16px' }}>
            {t('footer.description')}
          </p>
        </div>
        
        <div>
          <h4 style={{ color: 'var(--text)', marginBottom: '16px' }}>{t('footer.product')}</h4>
          <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <li><Link to="/" style={{ color: 'var(--text-muted)', textDecoration: 'none' }}>{t('nav.dashboard')}</Link></li>
            <li><Link to="/about" style={{ color: 'var(--text-muted)', textDecoration: 'none' }}>{t('nav.about')}</Link></li>
            <li><Link to="/tempmail" style={{ color: 'var(--text-muted)', textDecoration: 'none' }}>{t('nav.tempmail')}</Link></li>
            <li><Link to="/pgp" style={{ color: 'var(--text-muted)', textDecoration: 'none' }}>{t('nav.pgp')}</Link></li>
            <li><Link to="/upgrade" style={{ color: 'var(--text-muted)', textDecoration: 'none' }}>{t('nav.upgrade')}</Link></li>
          </ul>
        </div>
        
        <div>
          <h4 style={{ color: 'var(--text)', marginBottom: '16px' }}>{t('footer.resources')}</h4>
          <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <li><Link to="/contact" style={{ color: 'var(--text-muted)', textDecoration: 'none' }}>{t('nav.contact')}</Link></li>
            <li><a href="https://github.com/addisonelectronique" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--text-muted)', textDecoration: 'none' }}>GitHub</a></li>
            <li><a href="mailto:support@addisonelectronique.com" style={{ color: 'var(--text-muted)', textDecoration: 'none' }}>{t('nav.contact')}</a></li>
          </ul>
        </div>
        
        <div>
          <h4 style={{ color: 'var(--text)', marginBottom: '16px' }}>{t('footer.legal')}</h4>
          <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <li><Link to="/privacy" style={{ color: 'var(--text-muted)', textDecoration: 'none' }}>{t('footer.privacy')}</Link></li>
            <li><Link to="/terms" style={{ color: 'var(--text-muted)', textDecoration: 'none' }}>{t('footer.terms')}</Link></li>
          </ul>
        </div>
      </div>
      
      <div style={{ 
        maxWidth: '1400px', 
        margin: '40px auto 0',
        paddingTop: '20px',
        borderTop: '1px solid var(--border)',
        textAlign: 'center',
        color: 'var(--text-muted)'
      }}>
        © {new Date().getFullYear()} Addison Mail. {t('footer.copyright')}.
      </div>
    </footer>
  );
};

const AppContent = () => {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <div className="addison-bg"></div>
      <Navigation />
      
      <main style={{ 
        maxWidth: '1400px', 
        margin: '0 auto', 
        padding: '32px 24px',
        position: 'relative',
        zIndex: 1,
        flex: 1,
        width: '100%'
      }}>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/admin" element={<ProtectedRoute><Admin /></ProtectedRoute>} />
          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/inbox" element={<ProtectedRoute><Inbox /></ProtectedRoute>} />
          <Route path="/compose" element={<ProtectedRoute><Compose /></ProtectedRoute>} />
          <Route path="/tempmail" element={<TempMail />} />
          <Route path="/pgp" element={<ProtectedRoute><PGPManager /></ProtectedRoute>} />
          <Route path="/upgrade" element={<ProtectedRoute><Upgrade /></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
          <Route path="/messages/:id" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
        </Routes>
      </main>

      <Footer />
    </div>
  );
};

// CSS for dropdown
const dropdownStyles = `
  .dropdown {
    position: relative;
  }
  
  .dropdown-menu {
    position: absolute;
    top: 100%;
    right: 0;
    background: var(--card);
    border: 1px solid var(--border);
    border-radius: 12px;
    padding: 8px;
    min-width: 200px;
    box-shadow: var(--shadow-card);
    display: none;
    z-index: 1000;
  }
  
  .dropdown:hover .dropdown-menu {
    display: block;
  }
  
  .dropdown-item {
    display: block;
    padding: 12px 16px;
    color: var(--text);
    text-decoration: none;
    border-radius: 8px;
    transition: var(--transition);
    background: none;
    border: none;
    width: 100%;
    text-align: left;
    cursor: pointer;
    font-size: 14px;
  }
  
  .dropdown-item:hover {
    background: var(--card-hover);
  }
  
  hr {
    border: none;
    border-top: 1px solid var(--border);
    margin: 8px 0;
  }
`;

function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <Router>
          <style>{dropdownStyles}</style>
          <AppContent />
        </Router>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;




